// Every query and mutation of the calls screens. Keys are written here only,
// under ["ws", slug, ...] (a team event of the workspace drops just its part).
//
// Promise:
// - The list is every call in progress and the last 50 ended.
// - The ICE servers are kept 30 minutes, under a key outside the list's (the
//   list's invalidation would otherwise take them along and ask Cloudflare
//   again on every call event).
// - Starting and ending show at once and roll back on failure; starting
//   carries an Idempotency-Key.
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  IceServersView,
  VoiceRoomView,
} from "@trameria/cloud-api/voice-shared";
import {
  type Api,
  idempotencyKeyFor,
  newOptimisticId,
  optimisticMutation,
  useApi,
} from "@trameria/ui-core";

const enc = encodeURIComponent;

export const voicesKey = (slug: string) => ["ws", slug, "voices"] as const;

export function voicesQuery(api: Api, slug: string) {
  return queryOptions({
    queryKey: voicesKey(slug),
    queryFn: async () =>
      (await api<{ rooms: VoiceRoomView[] }>(`/api/w/${enc(slug)}/voices`))
        .rooms,
  });
}

export function iceServersQuery(api: Api, slug: string) {
  return queryOptions({
    queryKey: ["ws", slug, "voice-ice-servers"] as const,
    queryFn: () =>
      api<IceServersView>(`/api/w/${enc(slug)}/voices/ice-servers`),
    staleTime: 30 * 60_000,
  });
}

export interface StartCallInput {
  spaceId: string;
  title: string;
  /** For the row shown before the server answers. */
  meId: string;
  /** The same key when the same start is sent again. */
  clientId: string;
}

export function useStartCall(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartCallInput) =>
      api<{ room: VoiceRoomView }>(`/api/w/${enc(slug)}/voices`, {
        method: "POST",
        body: { spaceId: input.spaceId, title: input.title },
        idempotencyKey: idempotencyKeyFor({ clientId: input.clientId }),
      }),
    ...optimisticMutation<VoiceRoomView[], StartCallInput>(
      qc,
      voicesKey(slug),
      (old, input) => [
        {
          id: newOptimisticId(),
          spaceId: input.spaceId,
          title: input.title.trim(),
          createdByUserId: input.meId,
          createdAt: new Date().toISOString(),
          endedAt: null,
          participants: [],
        },
        ...(old ?? []),
      ],
    ),
  });
}

export function useEndCall(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roomId: string) =>
      api<{ room: VoiceRoomView }>(
        `/api/w/${enc(slug)}/voices/${enc(roomId)}/end`,
        { method: "POST" },
      ),
    ...optimisticMutation<VoiceRoomView[], string>(
      qc,
      voicesKey(slug),
      (old, roomId) => {
        const at = new Date().toISOString();
        return old?.map((r) => (r.id === roomId ? { ...r, endedAt: at } : r));
      },
    ),
  });
}
